"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Formatter = exports.NumberFormatTypes = void 0;
const global_constants_1 = require("@contentsquare/global-constants");
const number_formatter_1 = require("./number-formatter");
function toDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    seconds = seconds - hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds = Math.floor(seconds - minutes * 60);
    return { hours, minutes, seconds };
}
function isNumber(value) {
    return typeof value === 'number' && isFinite(value);
}
var NumberFormatTypes;
(function (NumberFormatTypes) {
    NumberFormatTypes["Number"] = "number";
    NumberFormatTypes["Integer"] = "integer";
    NumberFormatTypes["Rate"] = "rate";
    NumberFormatTypes["IntegerRate"] = "integerRate";
    NumberFormatTypes["Seconds"] = "seconds";
    NumberFormatTypes["Milliseconds"] = "milliseconds";
    NumberFormatTypes["Currency"] = "currency";
    NumberFormatTypes["Pixels"] = "pixels";
    NumberFormatTypes["Duration"] = "duration";
    NumberFormatTypes["Variation"] = "variation";
    NumberFormatTypes["Abbreviation"] = "abbreviation";
})(NumberFormatTypes = exports.NumberFormatTypes || (exports.NumberFormatTypes = {}));
const DEFAULT_FORMAT_OPTIONS = {
    locale: global_constants_1.SupportedLocale.En,
    currency: global_constants_1.CURRENCIES.find((cur) => cur.code === 'EUR').code,
    withUnit: true,
};
class Formatter {
    static getFormatter(locale, config) {
        const formatterKey = JSON.stringify(Object.assign(Object.assign({}, config), { locale }));
        let formatter = Formatter.formatters.get(formatterKey);
        if (!formatter) {
            formatter = new number_formatter_1.NumberFormatter(locale, config);
            Formatter.formatters.set(formatterKey, formatter);
        }
        return formatter;
    }
    static format(name) {
        const formatter = Formatter.formats[name];
        if (formatter) {
            return formatter;
        }
        return Formatter.number;
    }
    static number(input, options) {
        const { locale, digits } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        const minDigits = isNumber(digits) ? digits : getDigitsNumber(value);
        return Formatter.getFormatter(locale, {
            style: number_formatter_1.FormatterStyle.Decimal,
            minimumFractionDigits: minDigits,
            maximumFractionDigits: minDigits,
        }).format(value);
    }
    static integer(input, options) {
        const { locale } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        return Formatter.getFormatter(locale, {
            style: number_formatter_1.FormatterStyle.Decimal,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }
    static rate(input, options) {
        const { locale, withUnit, digits } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null) {
            return 'N/A';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        const minDigits = isNumber(digits) ? digits : getDigitsNumber(value);
        if (withUnit) {
            return Formatter.getFormatter(locale, {
                style: number_formatter_1.FormatterStyle.Percent,
                minimumFractionDigits: minDigits,
                maximumFractionDigits: minDigits,
            }).format(value);
        }
        else {
            return value.toString();
        }
    }
    static integerRate(input, options) {
        const { locale } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        return Formatter.getFormatter(locale, { style: number_formatter_1.FormatterStyle.Percent }).format(value);
    }
    static seconds(input, options) {
        return Formatter.time(input, Object.assign(Object.assign({}, options), { unit: 's' }));
    }
    static milliseconds(input, options) {
        return Formatter.time(input, Object.assign(Object.assign({}, options), { unit: 'ms', factor: 1000, digits: 0 }));
    }
    static time(input, options) {
        if (input === null)
            return '--';
        const { locale, withUnit, unit, factor, digits } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        let value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        if (factor) {
            value = factor * value;
        }
        if (withUnit) {
            return Formatter.number(value, { locale, digits }) + unit;
        }
        else {
            return Formatter.number(value, { locale, digits });
        }
    }
    static currency(input, options) {
        const { locale, withUnit, currency, digits } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        const minDigits = isNumber(digits) ? digits : getDigitsNumber(value);
        if (withUnit) {
            return Formatter.getFormatter(locale, {
                style: number_formatter_1.FormatterStyle.Currency,
                currency,
                minimumFractionDigits: minDigits,
                maximumFractionDigits: minDigits,
            }).format(value);
        }
        else {
            return Formatter.number(input, { locale, digits: minDigits });
        }
    }
    static pixels(input, options) {
        const { locale, withUnit } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        if (withUnit) {
            return Formatter.integer(input, { locale }) + 'px';
        }
        else {
            return Formatter.integer(input, { locale });
        }
    }
    static duration(input) {
        if (input === null)
            return '--';
        const duration = toDuration(input);
        if (duration.hours) {
            return duration.hours + ' h ' + to2digits(duration.minutes) + ' min ' + to2digits(duration.seconds) + ' s';
        }
        else if (duration.minutes) {
            return duration.minutes + ' min ' + to2digits(duration.seconds) + ' s';
        }
        return duration.seconds + ' s';
    }
    static variation(input, options) {
        const { locale } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        if (input === null)
            return '--';
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value))
            return '--';
        const rate = Formatter.rate(Math.abs(value), { locale });
        return value >= 0 ? `+ ${rate}` : `- ${rate}`;
    }
    static abbreviation(input, options) {
        const { locale, digits } = Object.assign(Object.assign({}, DEFAULT_FORMAT_OPTIONS), options);
        const units = ['K', 'M'];
        for (let i = units.length - 1; i >= 0; i--) {
            const decimal = Math.pow(1000, i + 1);
            if (input <= -decimal || input >= decimal) {
                return Formatter.number(input / decimal, { locale, digits }) + ' ' + units[i];
            }
        }
        return Formatter.number(input, { locale, digits });
    }
}
exports.Formatter = Formatter;
Formatter.formats = {
    [NumberFormatTypes.Number]: Formatter.number,
    [NumberFormatTypes.Integer]: Formatter.integer,
    [NumberFormatTypes.Seconds]: Formatter.seconds,
    [NumberFormatTypes.Milliseconds]: Formatter.milliseconds,
    [NumberFormatTypes.Rate]: Formatter.rate,
    [NumberFormatTypes.IntegerRate]: Formatter.integerRate,
    [NumberFormatTypes.Pixels]: Formatter.pixels,
    [NumberFormatTypes.Duration]: Formatter.duration,
    [NumberFormatTypes.Variation]: Formatter.variation,
    [NumberFormatTypes.Currency]: Formatter.currency,
    [NumberFormatTypes.Abbreviation]: Formatter.abbreviation,
};
Formatter.formatters = new Map();
// Returns 0 if number > 100, 1 if 10 <= number < 100, 2 if number < 10
function getDigitsNumber(value) {
    return Math.min(2, 2 - Math.min(2, Math.floor(Math.log10(Math.abs(value) || 1))));
}
function to2digits(value) {
    return ('0' + value).slice(-2);
}
//# sourceMappingURL=formatter.js.map